import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import type { LayoutNode } from '../../../components/oasis/bespoke/layout/types';
import type { Question } from '../../../components/oasis/types';
import type { RogerDocFormType } from '../../../services/api/models/RogerDocFormType';
import type {
  BespokeTemplateResponse,
  FormSchemaVersion,
  FormSchemaVersionPin,
  RogerDocFormTypeCatalogEntry,
} from '../../../services/api/types.gen';
import { fetchRogerEmrFormSchemaVersions } from '../fetchRogerEmrFormSchemaVersions';
import { bespokeApiErrorMessage } from '../formSchemaUtils';
import {
  formSchemaVersionPinKey,
  pinFromFormSchemaVersion,
} from '../formSchemaVersionPin';
import {
  type SectionPortCandidateGroup,
  type SectionPortDiscoveryResult,
  applySectionPortLayout,
  bindCandidatesToSharedPreviewGroups,
  discoverSectionPortCandidates,
  fetchSchemaTreeForVersion,
  groupCandidatesForSharedPreviewWork,
  layoutNodesDeepEqual,
} from './sectionLayoutPortShared';

export type SectionLayoutPortDialogToast = {
  success: (title: string, description?: string) => void;
  error: (title: string, description?: string) => void;
};

export type GroupRowState = {
  copying?: boolean;
  memberErrors?: Record<string, string>;
};

// Caps parallel schema-version list calls. The form-type catalog can list
// many visit types; without a cap, opening the dialog fires one request per
// visit type simultaneously.
const VERSION_FETCH_CONCURRENCY = 6;

async function mapWithConcurrency<T, R>(
  items: T[],
  limit: number,
  fn: (item: T) => Promise<R>
): Promise<R[]> {
  const results: R[] = new Array(items.length);
  let nextIndex = 0;

  async function worker() {
    while (nextIndex < items.length) {
      const index = nextIndex;
      nextIndex += 1;
      results[index] = await fn(items[index]);
    }
  }

  await Promise.all(
    Array.from({ length: Math.min(limit, items.length) }, worker)
  );
  return results;
}

export type UseSectionLayoutPortDialogOptions = {
  open: boolean;
  sourceLayout: LayoutNode[];
  /** Full RogerDoc pin for the source template — names alone are not unique. */
  sourcePin: FormSchemaVersionPin;
  sourceSectionName: string;
  sourceSectionSchema: Question[];
  formTypeCatalog: RogerDocFormTypeCatalogEntry[];
  toast: SectionLayoutPortDialogToast;
  /** Called with the rows that were actually saved (for local state patching). */
  onApplied?: (saved: BespokeTemplateResponse[]) => void;
};

/**
 * Discovery/apply state machine for {@link SectionLayoutPortDialog}, pulled
 * out of the component so the fetch/staleness/copy logic can be read, tested,
 * and reused independent of the ~800-line render tree — mirroring how
 * `useMatchingLayoutCopyDialog` separates the sibling "copy from matching
 * section" feature's logic from its JSX.
 */
export function useSectionLayoutPortDialog({
  open,
  sourceLayout,
  sourcePin,
  sourceSectionName,
  sourceSectionSchema,
  formTypeCatalog,
  toast,
  onApplied,
}: UseSectionLayoutPortDialogOptions) {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [failedVisitTypeLoads, setFailedVisitTypeLoads] = useState<string[]>(
    []
  );
  const [discovery, setDiscovery] = useState<SectionPortDiscoveryResult | null>(
    null
  );
  const [groupState, setGroupState] = useState<Record<string, GroupRowState>>(
    {}
  );
  const [copying, setCopying] = useState(false);
  const [previewGroupKey, setPreviewGroupKey] = useState<string | null>(null);
  const [allowInvalidOverride, setAllowInvalidOverride] = useState(false);

  /** Ignore stale `runDiscovery` completions (overlapping scans / Strict Mode). */
  const discoveryRunIdRef = useRef(0);

  const reset = useCallback(() => {
    discoveryRunIdRef.current += 1;
    setDiscovery(null);
    setError(null);
    setFailedVisitTypeLoads([]);
    setGroupState({});
    setPreviewGroupKey(null);
    setAllowInvalidOverride(false);
    setCopying(false);
    setLoading(false);
  }, []);

  const runDiscovery = useCallback(async () => {
    const runId = ++discoveryRunIdRef.current;

    setLoading(true);
    setError(null);
    setFailedVisitTypeLoads([]);
    try {
      const visitTypes = [
        ...new Set(formTypeCatalog.map(c => c.canonical_visit_type_id)),
      ];
      const versionsByPinKey = new Map<string, FormSchemaVersion>();
      const failedVisitTypes: string[] = [];
      await mapWithConcurrency(
        visitTypes,
        VERSION_FETCH_CONCURRENCY,
        async visitType => {
          try {
            const versions = await fetchRogerEmrFormSchemaVersions(visitType);
            for (const v of versions) {
              const versionPin = pinFromFormSchemaVersion(
                v.form_type as RogerDocFormType,
                v
              );
              if (versionPin) {
                versionsByPinKey.set(formSchemaVersionPinKey(versionPin), v);
              }
            }
          } catch (err) {
            console.error(
              '[section-port] failed to list schema versions for visit type',
              { visitType, cause: err }
            );
            failedVisitTypes.push(visitType);
          }
        }
      );

      const result = await discoverSectionPortCandidates({
        sourcePin,
        sourceSectionName,
        sourceSectionSchema,
        sourceLayout,
        resolveVersionByPin: candidatePin =>
          versionsByPinKey.get(formSchemaVersionPinKey(candidatePin)) ?? null,
        fetchSchemaForVersion: version =>
          fetchSchemaTreeForVersion(version, formTypeCatalog),
      });
      if (runId !== discoveryRunIdRef.current) return;

      // Same split + order as rendering (exact schema match section first,
      // then partial overlap) — see exactSchemaMatchGroups/
      // partialOverlapGroups/allWorkGroups below. Grouping the unsplit
      // candidate list here would pick its default by largest group size
      // across both sections, which can select a partial-overlap group even
      // when an exact-match group is visually first on screen.
      const workGroups = [
        ...groupCandidatesForSharedPreviewWork(
          result.candidates.filter(c => c.exactSchemaMatch)
        ),
        ...groupCandidatesForSharedPreviewWork(
          result.candidates.filter(c => !c.exactSchemaMatch)
        ),
      ];
      setDiscovery(result);
      setFailedVisitTypeLoads(failedVisitTypes.sort());
      setGroupState({});
      // A fresh scan always invalidates any prior "copy anyway" acknowledgment,
      // even if the same group signature is still selected afterward — the
      // checkbox means "I've seen and accepted *this* preview," and a rescan
      // can change what that preview is without changing previewGroupKey.
      setAllowInvalidOverride(false);
      setPreviewGroupKey(prev => {
        if (
          prev != null &&
          workGroups.some(g => g.destSchemaSignature === prev)
        ) {
          return prev;
        }
        return workGroups[0]?.destSchemaSignature ?? null;
      });
    } catch (e) {
      if (runId !== discoveryRunIdRef.current) return;
      setError(bespokeApiErrorMessage(e));
    } finally {
      if (runId === discoveryRunIdRef.current) {
        setLoading(false);
      }
    }
  }, [
    formTypeCatalog,
    sourceLayout,
    sourcePin,
    sourceSectionName,
    sourceSectionSchema,
  ]);

  useEffect(() => {
    if (!open) {
      reset();
      return;
    }
    void runDiscovery();
    // Only re-scan when the dialog opens or the source section identity
    // changes. `runDiscovery`'s own deps are the parent-supplied source
    // props (sourceLayout/formTypeCatalog/etc.) — the parent keeps those
    // referentially stable across unrelated re-renders (state / memoized),
    // so this effect only actually re-fires on open/section changes in
    // practice, not on every parent render.
  }, [open, sourcePin, sourceSectionName, reset, runDiscovery]);

  const exactSchemaMatchGroups = useMemo(
    () =>
      groupCandidatesForSharedPreviewWork(
        discovery?.candidates.filter(c => c.exactSchemaMatch) ?? []
      ),
    [discovery]
  );
  const partialOverlapGroups = useMemo(
    () =>
      groupCandidatesForSharedPreviewWork(
        discovery?.candidates.filter(c => !c.exactSchemaMatch) ?? []
      ),
    [discovery]
  );
  const exactSchemaMatchWorkCount = useMemo(
    () => exactSchemaMatchGroups.reduce((n, g) => n + g.members.length, 0),
    [exactSchemaMatchGroups]
  );
  const partialOverlapWorkCount = useMemo(
    () => partialOverlapGroups.reduce((n, g) => n + g.members.length, 0),
    [partialOverlapGroups]
  );
  const needsWorkCount = exactSchemaMatchWorkCount + partialOverlapWorkCount;

  const allWorkGroups = useMemo(
    () => [...exactSchemaMatchGroups, ...partialOverlapGroups],
    [exactSchemaMatchGroups, partialOverlapGroups]
  );
  // Unfiltered groups (includes members that already match the shared
  // layout), keyed by signature, so the "already up to date" badge/label can
  // report a group's real done/pending split instead of always seeing an
  // already-filtered work list where that count is trivially zero.
  const allBoundGroupsBySignature = useMemo(() => {
    const groups = bindCandidatesToSharedPreviewGroups(
      discovery?.candidates ?? []
    );
    return new Map(groups.map(g => [g.destSchemaSignature, g]));
  }, [discovery]);
  const workTemplateIds = useMemo(() => {
    const ids = new Set<string>();
    for (const group of allWorkGroups) {
      for (const member of group.members) {
        ids.add(member.template.id);
      }
    }
    return ids;
  }, [allWorkGroups]);
  // Audit list: candidates that won't receive the shared preview write.
  const alreadyMatch = useMemo(
    () =>
      discovery?.candidates.filter(c => !workTemplateIds.has(c.template.id)) ??
      [],
    [discovery, workTemplateIds]
  );

  const patchGroup = useCallback(
    (sig: string, patch: Partial<GroupRowState>) => {
      setGroupState(prev => ({
        ...prev,
        [sig]: { ...prev[sig], ...patch },
      }));
    },
    []
  );

  const selectNextPreviewGroup = useCallback(
    (removedSig: string, remaining: SectionPortCandidateGroup[]) => {
      if (previewGroupKey !== removedSig) return;
      setPreviewGroupKey(remaining[0]?.destSchemaSignature ?? null);
      setAllowInvalidOverride(false);
    },
    [previewGroupKey]
  );

  const handleCopySelectedGroup = useCallback(async () => {
    if (!previewGroupKey) return;
    const discoveryIncomplete =
      failedVisitTypeLoads.length > 0 ||
      (discovery?.unresolvedVersionNames.length ?? 0) > 0;
    if (discoveryIncomplete) {
      toast.error(
        'Re-scan required',
        'Discovery was incomplete. Run Re-scan before copying.'
      );
      return;
    }
    const group = allWorkGroups.find(
      g => g.destSchemaSignature === previewGroupKey
    );
    if (!group) return;

    const sig = group.destSchemaSignature;
    // Intentional, not a gap: Copy always applies to the whole selected
    // group (no per-destination picker). Same-schema group → one preview
    // layout written to every member, even if a member's live layout has
    // drifted from the representative's arrangement — consistency across
    // sections that share a schema is the point of this feature, so
    // overwriting that drift is the desired behavior. See
    // bindGroupToSharedPreviewLayout in sectionLayoutPortShared.ts.
    const layoutToSave = group.representative.resultLayout;
    setCopying(true);
    patchGroup(sig, { copying: true, memberErrors: {} });
    const memberErrors: Record<string, string> = {};
    const succeededIds: string[] = [];
    const savedTemplates: BespokeTemplateResponse[] = [];
    let ok = 0;
    let fail = 0;

    // Intentional, not a gap: `allowInvalidOverride` is one checkbox for the
    // whole selected group, applied uniformly to every member's write below.
    // The group shares a single representative preview/warning (see
    // bindGroupToSharedPreviewLayout), so there's no distinct per-member
    // warning to gate on — "copy anyway" is a group-level acknowledgment,
    // not a per-destination one. Do not add per-member validation here.
    for (const member of group.members) {
      try {
        const saved = await applySectionPortLayout({
          candidate: member,
          layout: layoutToSave,
          allowInvalidLayout: allowInvalidOverride,
        });
        succeededIds.push(member.template.id);
        savedTemplates.push(saved);
        ok++;
      } catch (e) {
        const msg = bespokeApiErrorMessage(e);
        console.error('[section-port] copy failed', {
          destination: member.displayLabel,
          error: msg,
          cause: e,
        });
        memberErrors[member.template.id] = msg;
        fail++;
      }
    }

    patchGroup(sig, { copying: false, memberErrors });
    setCopying(false);

    if (ok > 0) {
      onApplied?.(savedTemplates);
      // Patch `template` itself (not just the derived flags) with the saved
      // row. The next `bindGroupToSharedPreviewLayout` recompute reads
      // `template.layout_json` to decide `portResultMatchesExistingLayout` —
      // if we only set the flag here, that recompute (e.g. after copying a
      // sibling in the same group) would see the *old* layout_json and flip
      // this destination back to "needs work." Doing this locally avoids a
      // full `runDiscovery()` re-scan, which re-fetches every visit type's
      // schema versions and is too slow to run after every copy.
      const savedById = new Map(
        succeededIds.map((id, i) => [id, savedTemplates[i]])
      );
      setDiscovery(prev => {
        if (!prev) return prev;
        return {
          ...prev,
          candidates: prev.candidates.map(c => {
            const saved = savedById.get(c.template.id);
            if (!saved) return c;
            return {
              ...c,
              template: saved,
              portResultMatchesExistingLayout: true,
              // Recompute against the real source layout instead of
              // hard-coding false — an exact-schema-match destination that
              // was just copied can now be byte-identical to the source,
              // and should get the more precise "Already matches source"
              // label rather than the generic "Already up to date" one.
              layoutAlreadyMatches: layoutNodesDeepEqual(
                saved.layout_json,
                sourceLayout
              ),
            };
          }),
        };
      });

      if (fail === 0) {
        // Group fully copied — drop from work list and advance preview.
        const remaining = allWorkGroups.filter(
          g => g.destSchemaSignature !== sig
        );
        selectNextPreviewGroup(sig, remaining);
        setGroupState(prev => {
          const next = { ...prev };
          delete next[sig];
          return next;
        });
      }
    }

    if (fail === 0) {
      toast.success(
        'Layouts copied',
        `Spliced layout into ${ok} destination${ok === 1 ? '' : 's'}.`
      );
    } else if (ok === 0) {
      toast.error('Copy failed', `${fail} failed.`);
    } else {
      toast.error('Partial copy', `${ok} copied, ${fail} failed.`);
    }
  }, [
    allowInvalidOverride,
    allWorkGroups,
    discovery,
    failedVisitTypeLoads,
    onApplied,
    patchGroup,
    previewGroupKey,
    selectNextPreviewGroup,
    sourceLayout,
    toast,
  ]);

  const scanned = discovery != null;
  const discoveryIncomplete =
    scanned &&
    (failedVisitTypeLoads.length > 0 ||
      (discovery?.unresolvedVersionNames.length ?? 0) > 0);
  const busy = loading || copying;

  const previewGroup = useMemo(
    () =>
      previewGroupKey
        ? allWorkGroups.find(g => g.destSchemaSignature === previewGroupKey)
        : undefined,
    [allWorkGroups, previewGroupKey]
  );

  const selectedDestCount = previewGroup?.members.length ?? 0;
  const previewHasValidationWarning = Boolean(
    previewGroup?.representative.validationWarning
  );

  const handleCopyDebugBundle = useCallback(async () => {
    if (!previewGroup) {
      toast.error('Nothing to copy', 'Select a destination group first.');
      return;
    }
    const previewDestinationLayoutJson =
      previewGroup.representative.resultLayout;
    const bundle = {
      source_layout_json: sourceLayout,
      preview_destination_layout_json: previewDestinationLayoutJson,
    };
    try {
      await navigator.clipboard.writeText(JSON.stringify(bundle, null, 2));
      toast.success('Copied', 'Debug bundle copied to clipboard.');
    } catch (err) {
      console.error('Failed to copy section-port debug bundle:', err);
      toast.error('Clipboard failed', 'Could not write to clipboard.');
    }
  }, [previewGroup, sourceLayout, toast]);

  return {
    loading,
    error,
    failedVisitTypeLoads,
    discovery,
    groupState,
    copying,
    previewGroupKey,
    setPreviewGroupKey,
    allowInvalidOverride,
    setAllowInvalidOverride,
    runDiscovery,
    exactSchemaMatchGroups,
    partialOverlapGroups,
    exactSchemaMatchWorkCount,
    partialOverlapWorkCount,
    needsWorkCount,
    allBoundGroupsBySignature,
    alreadyMatch,
    handleCopySelectedGroup,
    scanned,
    discoveryIncomplete,
    busy,
    previewGroup,
    selectedDestCount,
    previewHasValidationWarning,
    handleCopyDebugBundle,
  };
}
